import { getSupabaseClient } from '../../../../core/supabase/client.js';
import type { ConfigurationDto } from '../dtos/ConfigurationDto.js';

export class ConfigurationRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  async list(orgId: string, projectId?: string | null): Promise<ConfigurationDto[]> {
    let query = this.supabase.from('configurations').select('*').eq('org_id', orgId);
    if (projectId !== undefined) {
      if (projectId) query = query.eq('project_id', projectId);
      else query = query.is('project_id', null);
    }
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return data ?? [];
  }

  async upsert(payload: Omit<ConfigurationDto, 'id'>): Promise<ConfigurationDto | null> {
    // Partial unique indexes can't be targeted by supabase upsert, so check-then-write
    let findQuery = this.supabase
      .from('configurations')
      .select('id')
      .eq('org_id', payload.org_id)
      .eq('feature_name', payload.feature_name);
    if (payload.project_id) findQuery = findQuery.eq('project_id', payload.project_id);
    else findQuery = findQuery.is('project_id', null);

    const { data: existing } = await findQuery.maybeSingle();

    if (existing?.id) {
      const { data, error } = await this.supabase
        .from('configurations')
        .update({ settings: payload.settings, description: payload.description })
        .eq('id', existing.id)
        .select('*')
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data ?? null;
    }

    const { data, error } = await this.supabase
      .from('configurations')
      .insert(payload)
      .select('*')
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ?? null;
  }

  async delete(id: string): Promise<void> {
    const { error } = await this.supabase.from('configurations').delete().eq('id', id);
    if (error) throw new Error(error.message);
  }
}
